// Job List ***************************************************************************************
// pathname: /v1/jobs
// ************************************************************************************************
import { useSwrInfiniteApi } from '@hooks/useSwrApi';
import { JOB_LIST_ITEMS_PER_PAGE } from 'src/constants';


// Interface
// ********************************************
interface JobListCardsInfiniteSwrProps {
  locale?: string;
  serverSideData?: ApiSchema<'CursorJobResult'>;
  limit?: number;
  urlParams?: string;
  initialCursor?: string;
}


/**
 * @description SWR: fetch job list cards with infinite pagination.
 *
 * * **locale**: string. What locale to use for the request.
 * * **serverSideData**: ApiJobListCards, optional. The data we got from the server side request (if we do it). Default undefined.
 * * **limit**: number, optional. The number of job cards to get per call. Modifies the *limit* url parameter (&limit=24). Default 24.
 * * **restUrlParams**: '&sort=relevant', optional. Default empty string.
 * * **initialCursor**: string, optional. The initial cursor to use for the request.
 */
export const useSwrJobListCardsInfinite = (props: JobListCardsInfiniteSwrProps) => {
  // Props destructuring
  // ********************************************
  const {
    locale,
    serverSideData,
    limit = JOB_LIST_ITEMS_PER_PAGE,
    urlParams = '',
    initialCursor,
  } = props;


  // Get data from API
  // ********************************************
  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  } = useSwrInfiniteApi<ApiSchema<'CursorJobResult'>>({
    url: '/v2/jobs',
    locale,
    urlParams,
    serverSideData,
    infiniteConfig: {
      paginationType: 'cursor',
      limit,
      // the key where the array of items is located in the response
      responseArrayKey: 'items',
      initialCursor,
      cursorPath: 'nextCursor',
    },
    apiOptions: {
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateOnMount: true, // Force revalidation on mount to sync with URL in SPA transitions
      revalidateFirstPage: true, // Sync the first page data immediately (nextCursor)
      dedupingInterval: 0, // Disable deduping interval to always fetch fresh data
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });


  // Return data
  // ********************************************
  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  };
};
